import axios from "axios";
import i18next from "i18next";
import { cachedRead } from "../../../offline";
import { createResource } from "../../../offline/resource";

const base = `${process.env.REACT_APP_BACKEND_URL}/site-docs`;
const token = () => localStorage.getItem("token");
const headers = () => ({ headers: { Authorization: "Bearer " + token() } });

const currentUserName = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  } catch {
    return "";
  }
};

// Entries and their photos can be made without a connection (src/offline): the photos wait on
// the device and are uploaded with the entry later. Reports and e-mail need the server.
const docs = createResource({
  module: "site-docs",
  base,
  auth: headers,
  idPath: "data._id",
  versionPath: "data.updatedAt",
  unwrap: data => data.data,
  wrap: view => ({ data: view }),
  label: body => body.title || (body.notes || "").slice(0, 60),
  blank: async (body, localId) => ({
    _id: localId,
    number: i18next.t("New", { ns: "dashboard" }),
    title: "",
    notes: "",
    photos: [],
    createdByName: currentUserName(),
    can: { update: true, delete: true },
    ...body
  }),
  toRow: view => ({
    ...view,
    notes: (view.notes || "").slice(0, 300),
    photoCount: (view.photos || []).length,
    cover: view.photos?.[0]?._id || null
  })
});

export const getMeta = () =>
  cachedRead("site-docs:meta", () => axios.get(`${base}/meta`, headers()));
export const listDocs = filters =>
  docs.list(JSON.stringify(filters || {}), () => axios.post(`${base}/list`, filters, headers()), {
    includeLocal: !filters?.page || filters.page === 1
  });
export const getDoc = id => docs.get(id);
export const createDoc = body => docs.create(body);
export const updateDoc = (id, body) => docs.update(id, body);
export const deleteDoc = id =>
  docs.remove(id, serverId => axios.delete(`${base}/${serverId}`, headers()));
export const deletePhoto = (id, photoId) =>
  docs.removePhoto(id, photoId, serverId =>
    axios.delete(`${base}/${serverId}/photos/${photoId}`, headers())
  );

export const uploadPhoto = (id, blob, { width, height, takenAt }) =>
  docs.addPhoto(
    id,
    blob,
    {
      width: String(width || 0),
      height: String(height || 0),
      ...(takenAt ? { takenAt: String(takenAt) } : {})
    },
    blob.type === "image/png" ? "photo.png" : "photo.jpg"
  );

export const transcribeAudio = (blob, language) => {
  const body = new FormData();
  body.append("audio", blob, "dictation.wav");
  body.append("language", language);
  return axios.post(`${base}/voice/transcribe`, body, headers());
};

// An <img> cannot send a header: the token goes in the query.
export const photoUrl = (id, photoId) =>
  docs.photoUrl(
    photoId,
    () => `${base}/${id}/photos/${photoId}?token=${encodeURIComponent(token() || "")}`
  );

// A blob error body (responseType "blob") turned back into JSON, so error alerts show the
// server's message.
const readBlobError = async error => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      error.response.data = JSON.parse(await data.text());
    } catch {
      // not JSON: keep it
    }
  }
  throw error;
};

const fileNameFrom = (res, fallback) => {
  const header = res.headers?.["content-disposition"] || "";
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) return decodeURIComponent(encoded[1]);
  const plain = /filename="([^"]+)"/i.exec(header);
  return plain ? plain[1] : fallback;
};

/** Photo report as PDF: body { ids } or { filters }, plus title, remark, lng, photoSize, showAuthors. */
export const reportPdf = body =>
  axios
    .post(`${base}/report`, body, { ...headers(), responseType: "blob" })
    .then(res => ({ blob: res.data, fileName: fileNameFrom(res, "Baustellendokumentation.pdf") }))
    .catch(readBlobError);

export const emailReport = body => axios.post(`${base}/report/email`, body, headers());

export const pdfUrl = (id, lng) =>
  `${base}/${id}/pdf?token=${encodeURIComponent(token() || "")}&lng=${encodeURIComponent(lng || "de")}`;
